// CAVEAT-START! IDE features like "Organize imports" in VSCode may alter the order of CSS files being imported, which result in visual regression in the app!
import 'react-date-range/dist/styles.css'; // should be the first
import 'react-date-range/dist/theme/default.css'; // should be the second

// CAVEAT-END!
import { Typography } from '@mui/material';
import { addYears, endOfYear, format, isSameDay, isWithinInterval, startOfYear, subYears } from 'date-fns';
import { CSSProperties, memo, useCallback, useContext, useMemo } from 'react';
import { Calendar, DateRange } from 'react-date-range';
import * as locales from 'react-date-range/dist/locale';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { GlobalDataContext } from '@/components/Global/GlobalDataManagement/GlobalDataManagement';
import { RequestWithDates } from '@/domain/newRequest';
import { DatePickerRange } from '@/domain/requestsPage';
import { BORDER_RADIUS, COLOR, COLOR_FUNCTION, FONT_SIZE, FONT_WEIGHT, PX_TO_REM } from '@/globalStyles';
import { CALENDAR_CONFIG } from '@/utils/constants/calendar';
import { DATE_FORMAT, getLocaleDateFormatEnum } from '@/utils/date/date';

import { useHolidays } from './hooks';

interface DayCssProperties extends CSSProperties {
    '--request-color': string;
}

export interface IDateRangePickerProps {
    onChange?: (dates: DatePickerRange) => void;
    range: DatePickerRange;
    oneDatePicker?: boolean;
    customConfig?: {
        // for more options check documentation https://github.com/hypeserver/react-date-range
        minDate?: Date;
        maxDate?: Date;
        rangeColors?: string[];
        disabledDates?: Date[];
        dateDisplayFormat?: DATE_FORMAT;
        shownDate?: Date;
        showDateDisplay?: boolean;
        editableDateInputs?: boolean;
        startDatePlaceholder?: string;
        endDatePlaceholder?: string;
        months?: number;
        onShownDateChange?: (dateRange: DatePickerRange, calendar) => void;
    };
    isUnstyled?: boolean;
    className?: string;
    showHolidays?: boolean;
    existedRequests?: RequestWithDates[];
    isDisabled?: boolean;
}

const INITIAL_CONFIG = {
    dateDisplayFormat: getLocaleDateFormatEnum(),
    monthDisplayFormat: DATE_FORMAT.MONTH_NAME_FULL_WITH_YEAR_NUMERIC,
    weekdayDisplayFormat: DATE_FORMAT.DAY_NAME_PURE,
    fixedHeight: true,
    months: 2, // months to show at time
    direction: 'horizontal', //months direction
    weekStartsOn: CALENDAR_CONFIG.weekStartsOn,
    showDateDisplay: false, // show date preview
    showMonthAndYearPickers: false, // show month/year select
    preventSnapRefocus: true, // prevent month change on select date
    minDate: startOfYear(subYears(new Date(), 2)),
    maxDate: endOfYear(addYears(new Date(), 2)),
};

const DateRangePicker = ({
    range,
    oneDatePicker,
    onChange,
    customConfig,
    showHolidays,
    existedRequests,
    className,
}: IDateRangePickerProps) => {
    const { i18n } = useTranslation();

    const handleDateChange = useCallback(
        (item: { range1: DatePickerRange }) => {
            if (!onChange) return;
            onChange(item.range1);
        },
        [onChange]
    );

    const handleOneDateChange = useCallback(
        (date: Date) => {
            if (!onChange) return;
            onChange({ startDate: date, endDate: date });
        },
        [onChange]
    );

    const config = useMemo(
        () => ({
            ...INITIAL_CONFIG,
            ...customConfig,
        }),
        [customConfig]
    );

    const globalData = useContext(GlobalDataContext);
    const organizationHolidays = useHolidays(globalData?.state?.organization?.holidays ?? [], {
        startDate: config.minDate,
        endDate: config.maxDate,
    });

    const customDayContent = useCallback(
        (day: Date) => {
            const isHoliday = showHolidays && organizationHolidays?.some(holiday => isSameDay(holiday, day));
            const requestDay = existedRequests?.find(request =>
                isWithinInterval(day, { start: request?.from, end: request?.to })
            );

            return (
                <Typography
                    variant="body2"
                    data-isholiday={isHoliday}
                    data-isrequestday={!!requestDay}
                    data-iscustom={requestDay?.isCustomType}
                    data-ishiddentype={requestDay?.isHiddenType}
                    style={{ '--request-color': requestDay?.color } as DayCssProperties}
                >
                    {format(day, DATE_FORMAT.DAY_PURE)}
                </Typography>
            );
        },
        [existedRequests, organizationHolidays, showHolidays]
    );

    return (
        <div className={className}>
            {oneDatePicker ? (
                <Calendar
                    locale={locales[i18n.language]}
                    onChange={handleOneDateChange}
                    date={range.startDate}
                    dayContentRenderer={customDayContent}
                    {...config}
                />
            ) : (
                <DateRange
                    locale={locales[i18n.language]}
                    onChange={handleDateChange}
                    ranges={[range]}
                    dayContentRenderer={customDayContent}
                    {...config}
                />
            )}
        </div>
    );
};

const Memoized = memo(DateRangePicker);
const Styled = styled(Memoized)`
    display: flex;
    justify-content: center;
    ${({ isUnstyled, isDisabled }) =>
        !isUnstyled &&
        css`
            .rdrCalendarWrapper {
                position: relative;
                padding-top: 1rem;
            }

            .rdrMonthAndYearPickers {
                display: none;
            }

            .rdrMonth {
                width: 16rem;
            }

            .rdrMonthName {
                text-transform: capitalize;
                text-align: center;
                font-weight: ${FONT_WEIGHT.medium};
                font-size: ${FONT_SIZE.smedium};
                color: ${COLOR.blackPearl};
                letter-spacing: 0.3px;
                padding: 0.4rem 0 0.8rem 0;
            }

            .rdrMonthAndYearWrapper {
                position: absolute;
                width: 100%;
                height: unset;
                padding-top: unset;
                & > button {
                    background-color: ${COLOR.white};
                    &:hover {
                        background-color: ${COLOR.aliceBlue};
                    }
                }
            }

            .inRage {
                color: ${COLOR.blackPearl};
            }

            .rdrDayPassive {
                visibility: hidden;
            }

            .rdrWeekDay {
                text-transform: uppercase;
            }

            .rdrDayHovered {
                border-radius: ${BORDER_RADIUS.round};
            }

            .rdrDay {
                border-radius: ${BORDER_RADIUS.round};
                z-index: 1;

                & > span:last-of-type p {
                    display: block;
                    width: 2rem;
                    height: 2rem;
                    line-height: 2rem;
                    border-radius: ${BORDER_RADIUS.round};
                }

                .rdrDayNumber {
                    color: ${COLOR.blackPearl};
                    p[data-isholiday='true'] {
                        background-color: ${COLOR_FUNCTION.separator};
                    }
                    p[data-isrequestday='true'] {
                        &[data-iscustom='true'] {
                            border: 1px dashed var(--request-color);
                        }
                        &[data-iscustom='false'] {
                            border: 1px solid var(--request-color);
                            &[data-ishiddentype='true'] {
                                background-color: var(--request-color);
                            }
                        }
                    }

                    &::after {
                        display: none;
                    }
                }

                &.rdrDay.rdrDayDisabled .rdrDayNumber {
                    color: ${COLOR.darkGray};
                }

                &.rdrDay:not(.rdrDayPassive) {
                    &.rdrDayToday .rdrDayNumber p {
                        font-weight: ${FONT_WEIGHT.bold};
                        color: ${COLOR.redWildWatermelon};
                        &:after {
                            display: none;
                        }
                    }

                    &.rdrDayToday {
                        .rdrInRange ~ .rdrDayNumber p {
                            color: ${COLOR.redWildWatermelon};
                        }
                    }

                    .rdrInRange {
                        background-color: ${COLOR.aliceBlue};
                        height: 2rem;
                        top: ${PX_TO_REM[2]};
                        & ~ .rdrDayNumber p {
                            color: ${COLOR.blackPearl};
                            border: none;
                            &[data-isholiday='true'],
                            &[data-isweekend='true'] {
                                background-color: unset;
                            }
                        }
                        .rdrDayToday ~ .rdrDayNumber p {
                            color: ${COLOR.redWildWatermelon};
                        }
                    }

                    .rdrStartEdge,
                    .rdrEndEdge,
                    .rdrSelected {
                        background-color: ${COLOR.blue};
                        border-radius: ${BORDER_RADIUS.round};
                        top: ${PX_TO_REM[2]};
                        left: ${PX_TO_REM[2]};
                        right: ${PX_TO_REM[2]};
                        height: 2rem;
                        & ~ .rdrDayNumber p {
                            color: ${COLOR.white};
                            border: none;
                            &[data-isholiday='true'],
                            &[data-isweekend='true'] {
                                background-color: unset;
                            }
                        }
                    }

                    .rdrStartEdge:after,
                    .rdrEndEdge:before {
                        content: '';
                        position: absolute;
                        width: 100%;
                        height: 100%;
                        z-index: -1;
                        background-color: ${COLOR.aliceBlue};
                    }

                    .rdrStartEdge.rdrEndEdge:after,
                    .rdrStartEdge.rdrEndEdge:before {
                        display: none;
                    }

                    .rdrStartEdge:after {
                        left: ${PX_TO_REM[2]};
                        border-top-left-radius: ${BORDER_RADIUS.round};
                        border-bottom-left-radius: ${BORDER_RADIUS.round};
                    }

                    .rdrEndEdge:before {
                        right: ${PX_TO_REM[2]};
                        border-top-right-radius: ${BORDER_RADIUS.round};
                        border-bottom-right-radius: ${BORDER_RADIUS.round};
                    }

                    .rdrDayStartPreview,
                    .rdrDayEndPreview,
                    .rdrDayInPreview {
                        padding: 1.02rem;
                        top: 0.6px;
                        border-color: ${COLOR.blue};
                        & ~ .rdrDayNumber p {
                            border: none;
                        }
                    }

                    .rdrDayStartPreview {
                        border-top-left-radius: ${BORDER_RADIUS.round};
                        border-bottom-left-radius: ${BORDER_RADIUS.round};
                        left: ${PX_TO_REM[1]};
                    }

                    .rdrDayEndPreview {
                        border-top-right-radius: ${BORDER_RADIUS.round};
                        border-bottom-right-radius: ${BORDER_RADIUS.round};
                        right: ${PX_TO_REM[1]};
                    }
                }
                ${isDisabled &&
                css`
                    pointer-events: none;
                `}
            }

            .rdrDayStartOfMonth,
            .rdrDayStartOfWeek {
                .rdrEndEdge ~ .rdrDayEndPreview {
                    border: none;
                }
                .rdrStartEdge ~ .rdrDayStartPreview {
                    border-left: none;
                }
            }

            .rdrDayEndOfMonth,
            .rdrDayEndOfWeek {
                .rdrStartEdge ~ .rdrDayStartPreview {
                    border: none;
                }
                .rdrEndEdge ~ .rdrDayEndPreview {
                    border-right: none;
                }
            }
        `}
`;

export default Styled;
